import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { CloseOutlined, NearMeOutlined, PlaceOutlined } from "@mui/icons-material";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";

import findAllPlacesWithIqLocation, { PlacesViewbox } from "../../../../resources/Api/location/findAllPlacesWithIqLocation";
import Rmodal from "../../../../components/Rmodal";

import {
    Autocomplete,
    Box,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Paper,
    TextField,
    Typography,
} from "@mui/material";

import { Map } from "leaflet";

const mapSearchFieldSx = {
    width: "100%",
    "& .MuiOutlinedInput-root": {
        minHeight: 56,
        borderRadius: 2,
        backgroundColor: "#ffffff",
        transition: "box-shadow 200ms ease, border-color 200ms ease",
        "& fieldset": { borderColor: "rgba(0, 63, 74, 0.24)" },
        "&:hover fieldset": { borderColor: "#006C77" },
        "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(0, 183, 0, 0.12)" },
        "&.Mui-focused fieldset": { borderColor: "#00B700" },
    },
    "& .MuiInputLabel-root": { fontFamily: "Prometo" },
};

// Quantos resultados bastam para não ampliar a área, e quantas vezes a área visível vale a "região ao redor"
const MIN_AREA_RESULTS = 3;
const SURROUNDING_AREA_FACTOR = 6;

const expandViewbox = (box: PlacesViewbox, factor: number): PlacesViewbox => {
    const lngPad = ((box.east - box.west) * (factor - 1)) / 2;
    const latPad = ((box.north - box.south) * (factor - 1)) / 2;
    return {
        west: box.west - lngPad,
        east: box.east + lngPad,
        south: Math.max(-90, box.south - latPad),
        north: Math.min(90, box.north + latPad),
    };
};

const MapSearch = ({
    isOpen,
    mapRef,
    loading,
    selectedMap,
    style,
    setShowMapSearch,
    withToolbar = true,
    onSearchNearestVehicle,
}: {
    isOpen: boolean;
    mapRef: React.MutableRefObject<google.maps.Map & Map>;
    loading: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    selectedMap: string;
    style: React.CSSProperties;
    setShowMapSearch: (value: boolean) => void | undefined;
    withToolbar: boolean;
    onSearchNearestVehicle?: (point: { lat: number; lng: number; address: string }) => void;
}) => {
    const [predictionResults, setPredictionResults] = useState<Array<any>>([]);
    const [query, setQuery] = useState<string>("");
    const [canSeach, setCanSearch] = useState<boolean>(false);
    const [typingTimer, setTypingTimer] = useState<any>(0);
    const [searchNearestVehicle, setSearchNearestVehicle] = useState<boolean>(false);
    const [isSearchingAddress, setIsSearchingAddress] = useState<boolean>(false);
    const autcompleteRef = useRef<any>(null);

    // Área visível do mapa aberto (Google Maps ou Leaflet); sem mapa pronto, busca sem limitar a área
    const getVisibleViewbox = useCallback((): PlacesViewbox | undefined => {
        const bounds = (mapRef?.current as any)?.getBounds?.();
        if (!bounds) return undefined;

        if (selectedMap === "gmaps") {
            const southWest = bounds.getSouthWest();
            const northEast = bounds.getNorthEast();
            return { west: southWest.lng(), north: northEast.lat(), east: northEast.lng(), south: southWest.lat() };
        }
        return { west: bounds.getWest(), north: bounds.getNorth(), east: bounds.getEast(), south: bounds.getSouth() };
    }, [mapRef, selectedMap]);

    const getPlacesPredictions = useCallback(async () => {
        setIsSearchingAddress(true);
        try {
            // Em etapas, parando na primeira com resultado suficiente: o que está na tela, uma área maior ao
            // redor (a cidade, para quem olha um bairro) e, por fim, o Brasil com preferência pela região.
            const visible = getVisibleViewbox();
            const steps: Array<{ viewbox?: PlacesViewbox; bounded: boolean }> = visible
                ? [
                    { viewbox: visible, bounded: true },
                    { viewbox: expandViewbox(visible, SURROUNDING_AREA_FACTOR), bounded: true },
                    { viewbox: expandViewbox(visible, SURROUNDING_AREA_FACTOR), bounded: false },
                ]
                : [{ bounded: false }];

            let best: Array<any> = [];
            for (const step of steps) {
                const { data } = await findAllPlacesWithIqLocation(query, step.viewbox, step.bounded);
                const results = Array.isArray(data) ? data : [];
                if (results.length > best.length) best = results;
                if (results.length >= MIN_AREA_RESULTS) break;
            }
            setPredictionResults(best);
        } catch (error) {
            console.log(error);
            setPredictionResults([]);
        } finally {
            setIsSearchingAddress(false);
        }
    }, [query, getVisibleViewbox]);

    useEffect(() => {
        if (canSeach) {
            getPlacesPredictions();
        }
        setCanSearch(false);
    }, [canSeach, getPlacesPredictions]);

    return (
        <>
            <Rmodal
                open={isOpen}
                hiddenBackdrop
                style={{ transition: "all 0.8s ease-out allow-discrete" }}
            >
                <Paper
                    role="dialog"
                    aria-labelledby="map-search-title"
                    className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-shadow-2xl"
                    sx={{
                        ...style,
                        position: "absolute",
                        top: { xs: 72, sm: 88 },
                        left: "50%",
                        width: { xs: "calc(100vw - 88px)", sm: 500 },
                        maxWidth: 500,
                        padding: 0,
                        transform: "translateX(-50%)",
                        overflow: "visible",
                        border: "1px solid rgba(0, 63, 74, 0.16)",
                        backgroundColor: "#ffff",
                        boxShadow: "0 24px 70px rgba(0, 0, 0, 0.28)",
                        borderRadius: 4,
                    }}
                >
                    {withToolbar && (
                        <Box
                            className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-bg-gtr-sidebar tw-px-5 tw-py-4 tw-text-white"
                            sx={{
                                paddingX: { xs: 2, sm: 3 },
                                paddingY: 2,
                                backgroundImage: "linear-gradient(to right, #006C77, #09414A)",
                                borderTopLeftRadius: "inherit",
                                borderTopRightRadius: "inherit",
                            }}
                        >
                            <Box className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
                                <Box
                                    className="tw-flex tw-h-14 tw-w-14 tw-flex-none tw-items-center tw-justify-center tw-rounded-xl tw-bg-white/10"
                                    sx={{ width: 56, height: 56, flexShrink: 0, borderRadius: 3, backgroundColor: "rgba(255,255,255,0.10)" }}
                                >
                                    <PlaceOutlined sx={{ fontSize: 30 }} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography id="map-search-title" sx={{ color: "inherit", fontFamily: "Prometo", fontSize: { xs: 18, sm: 21 }, fontWeight: 700 }}>
                                        Busca de endereço
                                    </Typography>
                                </Box>
                            </Box>
                            <Tooltip title="Fechar" arrow placement="left">
                                <IconButton
                                    aria-label="Fechar busca de endereço"
                                    size="small"
                                    onClick={() => setShowMapSearch(false)}
                                    className="!tw-bg-white/10 !tw-text-white tw-transition-all tw-duration-200 hover:!tw-bg-red-500/70"
                                >
                                    <CloseOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    <Box className="tw-rounded-b-2xl tw-bg-white tw-p-4 sm:tw-p-6" sx={{ padding: { xs: 2, sm: 3 }, backgroundColor: "#ffff" }}>
                            <Autocomplete
                                className="search-place"
                                ref={(ref) => (autcompleteRef.current = ref)}
                                disablePortal
                                options={predictionResults}
                                noOptionsText="Sem resultados"
                                loadingText="Buscando endereços..."
                                getOptionLabel={(option) => `${option.display_name}`}
                                onChange={(_, option) => {
                                    if (!option) return;

                                    const lat = Number(option.lat);
                                    const lng = Number(option.lon);

                                    if (selectedMap === "gmaps") {
                                        mapRef?.current?.panTo({ lat, lng });
                                    } else {
                                        mapRef?.current?.flyTo({ lat, lng });
                                    }

                                    setPredictionResults([]);
                                    setQuery("");

                                    if (searchNearestVehicle && onSearchNearestVehicle) {
                                        onSearchNearestVehicle({ lat, lng, address: option.display_name });
                                    }
                                    setShowMapSearch(false);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        autoComplete="off"
                                        label="Endereço"
                                        placeholder="Digite um endereço ou local"
                                        InputLabelProps={{ shrink: true }}
                                        sx={mapSearchFieldSx}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loading || isSearchingAddress ? <CircularProgress color="inherit" size={18} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                loading={loading || isSearchingAddress}
                                onInputChange={(event, newInputValue) => {
                                    clearTimeout(typingTimer);
                                    setQuery(newInputValue);
                                }}
                                onKeyDown={(event) =>
                                    event.key === "Enter" ? setCanSearch(true) : null
                                }
                                onKeyUp={(event) => {
                                    clearTimeout(typingTimer); // Limpa o temporizador anterior
                                    setTypingTimer(
                                        setTimeout(() => {
                                            if (query) {
                                                setCanSearch(true);
                                            }
                                        }, 1500)
                                    );
                                }}
                                renderOption={(props, option) => {
                                    const { key } = props;
                                    return (
                                        <MenuItem
                                            {...props}
                                            key={key}
                                            component="li"
                                            className="!tw-m-1.5 !tw-whitespace-normal !tw-rounded-lg !tw-px-3 !tw-py-2.5 !tw-font-prometo hover:!tw-bg-emerald-50"
                                        >
                                            {`${option.display_name}`}
                                        </MenuItem>
                                    );
                                }}
                            />

                            {onSearchNearestVehicle && (
                                <FormControlLabel
                                    sx={{ mt: 1 }}
                                    control={
                                        <Checkbox
                                            checked={searchNearestVehicle}
                                            onChange={(event) => setSearchNearestVehicle(event.target.checked)}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <NearMeOutlined fontSize="small" sx={{ mr: 0.5 }} />
                                            <Typography variant="body2" sx={{ fontFamily: "Prometo" }}>
                                                Buscar veículo mais próximo deste endereço
                                            </Typography>
                                        </Box>
                                    }
                                />
                            )}
                    </Box>
                </Paper>
            </Rmodal>
        </>
    );
};

export default MapSearch;
